import React, { useState, useEffect } from 'react';
import { X, ShieldCheck, Lock, User, BookOpen } from 'lucide-react';
import { createStudent, fetchGrades } from '../api/studentApi';
import './AddChildModal.css';

const AddChildModal = ({ onClose, onSuccess }) => {
  const [grades, setGrades] = useState([]);
  const [formData, setFormData] = useState({ studentUsername: '', studentPassword: '', gradeId: '' });
  const [loading, setLoading] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  // 1. Sanitization: Strips spaces & '@' immediately as user types
  const handleUsernameChange = (e) => {
    const sanitized = e.target.value.replace(/[\s@]/g, '').toLowerCase();
    setFormData(prev => ({ ...prev, studentUsername: sanitized }));

    // Clear error while typing
    if (usernameError) setUsernameError('');
  };

  // 2. Format Validation: Validates first.last structure when clicking away
  const validateUsername = () => {
    const val = formData.studentUsername;
    if (val && !/^[a-z0-9]+(?:\.[a-z0-9]+)+$/.test(val)) {
      setUsernameError("Format must be 'first.last' (e.g., alex.smith)");
      return false;
    }
    setUsernameError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Re-verify username format before firing the API request
    if (!validateUsername()) {
      return;
    }

    setLoading(true);
    try {
      await createStudent(formData);
      onSuccess();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchGradesList = async () => {
      try {
        const response = await fetchGrades();
        setGrades(response.data);
        if (response.data.length > 0) {
          setFormData(prev => ({ ...prev, gradeId: response.data[0].id }));
        }
      } catch (err) {
        console.error("Failed to load grades", err);
      }
    };
    fetchGradesList();
  }, []);

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="close-x" onClick={onClose}><X /></button>
        <div className="modal-header">
          <ShieldCheck size={40} color="#4f46e5" />
          <h2>New Student Account</h2>
          <p>Children don't need emails. Just a fun username!</p>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">

          {/* Username Input with Validation */}
          <div className="modal-input-group">
            <div className={`modal-input ${usernameError ? 'has-error' : ''}`}>
              <User size={18} />
              <input
                type="text"
                placeholder="Child's Username (naruto.uzumaki)"
                value={formData.studentUsername}
                required
                autoComplete="one-time-code" 
                onChange={handleUsernameChange}
                onBlur={validateUsername}
              />
            </div>
          </div>

          <div className="modal-input">
            <Lock size={18} />
            <input
              type="password"
              placeholder="Password"
              required
              autoComplete="new-password" 
              value={formData.studentPassword}
              onChange={(e) => setFormData({ ...formData, studentPassword: e.target.value })}
            />
          </div>

          {/* Grade Dropdown */}
          <div className="modal-input">
            <BookOpen size={18} />
            <select
              value={formData.gradeId}
              onChange={(e) => setFormData({ ...formData, gradeId: e.target.value })}
              required
              className="dynamic-select"
            >
              {grades.length === 0 ? (
                <option>Loading grades...</option>
              ) : (
                grades.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))
              )}
            </select>
          </div>

          <button type="submit" className="btn primary-btns btn-full" disabled={loading}>
            {loading ? "Creating..." : "Create Account"}
          </button>
          {usernameError && (<div className="modal-error-text">User name format must be 'first.last' (e.g., alex.smith)</div>)}
        </form>
      </div>
    </div>
  );
};

export default AddChildModal;